import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Derives a personalized first-day plan from the BuddyCheckQuiz answers.
 * Pure function — no API call. Keeps onboarding fast and offline-friendly.
 */
function derivePlan(answers, isDe) {
    if (!answers || typeof answers !== 'object') return [];

    const items = [];
    const goal = answers.goal;
    const sleep = parseInt(answers.sleep_hours, 10);
    const fitness = answers.fitness_level;

    // 1. Goal-driven primary action
    const goalMap = {
        weight_loss: {
            de: { title: 'Heute: 1 Mahlzeit fotografieren', body: 'Lade die erste Mahlzeit hoch — Nanouk lernt deine Routine in 3 Tagen.', to: '/nutrition' },
            en: { title: 'Today: photograph 1 meal', body: 'Log your first meal — Nanouk learns your routine in 3 days.', to: '/nutrition' },
        },
        muscle: {
            de: { title: 'Heute: Workout & Protein-Log', body: 'Logge dein nächstes Training + die erste Protein-Quelle des Tages.', to: '/fitness' },
            en: { title: 'Today: workout & protein log', body: 'Log your next workout + the first protein source of the day.', to: '/fitness' },
        },
        energy: {
            de: { title: 'Heute: Stimmung & Wasser tracken', body: 'Tap auf das Mood-Widget + +250 ml Wasser — die Basis für Energy-Insights.', to: '/' },
            en: { title: 'Today: track mood & water', body: 'Tap the mood widget + +250 ml water — the foundation for energy insights.', to: '/' },
        },
        health: {
            de: { title: 'Heute: Labor-Befund hochladen', body: 'Wenn du einen aktuellen Befund hast, lade ihn auf der Health-Seite hoch — Nanouk macht daraus konkrete Quests.', to: '/health' },
            en: { title: 'Today: upload a lab report', body: 'If you have a recent report, upload it on the Health page — Nanouk turns it into concrete quests.', to: '/health' },
        },
        sleep: {
            de: { title: 'Heute Abend: Schlafenszeit loggen', body: 'Nutze die Default-Bettzeit in Einstellungen, sodass Nanouk dich morgen mit einem Schlaf-Insight begrüßen kann.', to: '/sleep' },
            en: { title: 'Tonight: log your bedtime', body: 'Set a default bedtime in Settings so Nanouk can greet you with a sleep insight tomorrow.', to: '/sleep' },
        },
    };
    const primary = goalMap[goal] || goalMap.health;
    items.push({ id: 'goal', ...(isDe ? primary.de : primary.en) });

    // 2. Sleep-conditional action
    if (Number.isFinite(sleep) && sleep < 7) {
        items.push({
            id: 'sleep',
            title: isDe ? 'Diese Woche: 30 Min früher ins Bett' : 'This week: 30 min earlier to bed',
            body: isDe
                ? 'Du schläfst unter 7h. Schon 30 Min mehr verbessern Energie & Stimmung sichtbar.'
                : 'You sleep under 7h. Just 30 more minutes will visibly boost energy & mood.',
            to: '/sleep',
        });
    }

    // 3. Fitness-conditional fallback (only if we have < 3 items so far)
    if (items.length < 3) {
        items.push({
            id: 'fitness',
            title: fitness === 'beginner'
                ? (isDe ? 'Diese Woche: 1 Spaziergang loggen' : 'This week: log 1 walk')
                : (isDe ? 'Diese Woche: 2 Workouts loggen' : 'This week: log 2 workouts'),
            body: isDe
                ? 'Klein anfangen — Nanouk generiert daraus deine erste Weekly Quest.'
                : 'Start small — Nanouk will spin up your first Weekly Quest from this.',
            to: '/fitness',
        });
    }

    // Always end with a "talk to Nanouk" anchor
    if (items.length < 3) {
        items.push({
            id: 'coach',
            title: isDe ? 'Sag Nanouk Hallo' : 'Say hi to Nanouk',
            body: isDe
                ? 'Frag sie alles über deinen Plan — sie kennt jetzt schon dein Ziel.'
                : 'Ask her anything about your plan — she already knows your goal.',
            to: '/coach',
        });
    }

    return items.slice(0, 3);
}

export function FirstDayPlanSheet() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const navigate = useNavigate();

    // Read pending state synchronously so we don't trigger a re-render later.
    const [answers] = useState(() => {
        try {
            const pending = localStorage.getItem('bestbuddy_first_day_plan_pending');
            const raw = localStorage.getItem('bestbuddy_quiz_answers');
            if (pending !== '1' || !raw) return null;
            return JSON.parse(raw);
        } catch (_e) { return null; }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!answers) return undefined;
        // Small delay so it doesn't fight other onboarding toasts on first paint
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
    }, [answers]);

    const plan = useMemo(() => derivePlan(answers, isDe), [answers, isDe]);

    const handleDismiss = () => {
        setOpen(false);
        try {
            localStorage.removeItem('bestbuddy_first_day_plan_pending');
            // Keep quiz answers for later — they may be useful for the coach context
        } catch (_e) { /* noop */ }
    };

    const handleAction = (to) => {
        handleDismiss();
        if (to) navigate(to);
    };

    if (!plan.length) return null;

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
            <SheetContent
                side="bottom"
                className="rounded-t-3xl max-h-[88vh] overflow-y-auto pt-6"
                data-testid="first-day-plan-sheet"
            >
                <SheetHeader className="text-left space-y-2 mb-4">
                    <SheetTitle className="text-xl font-display">
                        {isDe ? '✨ Dein erster Tag mit Nanouk 🐺' : '✨ Your first day with Nanouk 🐺'}
                    </SheetTitle>
                    <SheetDescription>
                        {isDe
                            ? 'Basierend auf deinem Buddy-Check — drei kleine Schritte, die heute den Unterschied machen.'
                            : 'Based on your Buddy-Check — three small steps that make today matter.'}
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-3">
                    {plan.map((item, i) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => handleAction(item.to)}
                            className="w-full text-left flex items-start gap-3 p-4 rounded-2xl border border-border bg-card hover:border-cyan-500/40 transition-colors group"
                            data-testid={`first-day-plan-item-${item.id}`}
                        >
                            <div className="w-8 h-8 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-sm font-bold text-cyan-600 dark:text-cyan-400 flex-shrink-0">
                                {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm leading-snug">{item.title}</p>
                                <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                                    {item.body}
                                </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0 mt-1" />
                        </button>
                    ))}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <Button
                        onClick={handleDismiss}
                        className="flex-1 gap-2"
                        data-testid="first-day-plan-start-btn"
                    >
                        <CheckCircle2 className="w-4 h-4" />
                        {isDe ? "Verstanden — los geht's" : "Got it — let's go"}
                    </Button>
                </div>

                <p className="text-[11px] text-center text-muted-foreground mt-4">
                    {isDe
                        ? 'Du findest deinen Plan jederzeit unter „Hilfe & Tutorials".'
                        : 'You can revisit your plan any time under "Help & Tutorials".'}
                </p>
            </SheetContent>
        </Sheet>
    );
}
